import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  Badge,
  Tooltip,
  Paper,
  Stack,
  Tab,
  Tabs
} from '@mui/material';
import {
  Business,
  Email,
  Phone,
  LocationOn,
  Save,
  PhotoCamera,
  AttachMoney,
  Language,
  Backup,
  Security,
  Settings,
  Public,
  Storage,
  People,
  Cloud,
  Lock,
  Schedule,
  Notifications,
  Assessment,
  Edit,
  CheckCircle,
  Warning,
  Info
} from '@mui/icons-material';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role='tabpanel'
    hidden={value !== index}
    {...other}
  >
    {value === index && (
      <Box sx={{ py: 3 }}>
        {children}
      </Box>
    )}
  </div>
);

const CompanySettings = () => {
  const [tabValue, setTabValue] = useState(0);
  const [companyData, setCompanyData] = useState({
    name: 'Poultry Management System',
    email: 'info@poultrymanagement.com',
    phone: '+255 XXX XXX XXX',
    address: 'Dar es Salaam, Tanzania',
    website: 'www.poultrymanagement.com',
    taxId: 'TIN-XXXXXXXXX',
    registrationNumber: 'REG-XXXXXXXXX',
    currency: 'TZS',
    language: 'English',
    timeZone: 'Africa/Dar_es_Salaam',
    description: 'Leading poultry management solutions in East Africa',
    established: '2020',
    industry: 'Agriculture & Livestock'
  });

  const [systemSettings, setSystemSettings] = useState({
    autoBackup: true,
    emailNotifications: true,
    dataRetention: 365,
    enableTwoFactor: false,
    maintenanceMode: false,
    allowGuestAccess: false,
    sessionTimeout: 30,
    maxLoginAttempts: 5
  });

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setCompanyData(prev => ({ ...prev, [name]: value }));
  };

  const handleSystemChange = (name) => (event) => {
    setSystemSettings(prev => ({ 
      ...prev, 
      [name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value 
    }));
  };

  const handleSaveCompany = async () => {
    try {
      console.log('Updating company settings:', companyData);
      setSuccess('Company settings updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to update company settings');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleSaveSystem = async () => {
    try {
      console.log('Updating system settings:', systemSettings);
      setSuccess('System settings updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to update system settings');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleBackupNow = async () => {
    try {
      console.log('Initiating backup...');
      setSuccess('Backup initiated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to initiate backup');
      setTimeout(() => setError(''), 3000);
    }
  };

  // System status data
  const systemStatus = {
    serverStatus: 'Online',
    lastBackup: new Date().toLocaleDateString(),
    storageUsed: 1.2,
    storageTotal: 10,
    activeUsers: 3,
    uptime: '99.9%'
  };

  const storagePercentage = (systemStatus.storageUsed / systemStatus.storageTotal) * 100;

  return (
    <Box>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 4,
          mb: 3,
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              mr: 3,
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Business sx={{ fontSize: 40 }} />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Company Settings
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              {companyData.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Manage your business profile and system preferences
            </Typography>
          </Box>
          <Tooltip title="Upload Company Logo">
            <IconButton 
              sx={{ 
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': { background: 'rgba(255,255,255,0.3)' }
              }}
            >
              <PhotoCamera />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <Typography variant="h6" color="white">{systemStatus.activeUsers}</Typography>
              <Typography variant="caption" color="white" sx={{ opacity: 0.8 }}>Active Users</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <Typography variant="h6" color="white">{systemStatus.uptime}</Typography>
              <Typography variant="caption" color="white" sx={{ opacity: 0.8 }}>Uptime</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <Typography variant="h6" color="white">{systemStatus.storageUsed}GB</Typography>
              <Typography variant="caption" color="white" sx={{ opacity: 0.8 }}>Storage Used</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <CheckCircle sx={{ color: 'white', mb: 1 }} />
              <Typography variant="caption" color="white" sx={{ opacity: 0.8 }}>System Online</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label="Company Profile" 
            icon={<Business />} 
            iconPosition="start"
          />
          <Tab 
            label="System Settings" 
            icon={<Settings />} 
            iconPosition="start"
          />
          <Tab 
            label="Security & Backup" 
            icon={<Security />} 
            iconPosition="start"
          />
        </Tabs>

        {/* Company Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} lg={8}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Business color="primary" sx={{ mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6">Business Information</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Core details about your company
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="name"
                        label="Company Name"
                        value={companyData.name}
                        onChange={handleCompanyChange}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: <Business sx={{ color: 'action.active', mr: 1 }} />
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="industry"
                        label="Industry"
                        value={companyData.industry}
                        onChange={handleCompanyChange}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="description"
                        label="Company Description"
                        value={companyData.description}
                        onChange={handleCompanyChange}
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        helperText="Brief description of your business"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="established"
                        label="Year Established"
                        value={companyData.established}
                        onChange={handleCompanyChange}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="website"
                        label="Website"
                        value={companyData.website}
                        onChange={handleCompanyChange}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: <Public sx={{ color: 'action.active', mr: 1 }} />
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Actions */}
            <Grid item xs={12} lg={4}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <Assessment sx={{ mr: 1 }} />
                    System Overview
                  </Typography>
                  
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Server Status</Typography>
                      <Chip 
                        label={systemStatus.serverStatus} 
                        color="success" 
                        size="small"
                        icon={<CheckCircle />}
                      />
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Storage Usage
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={storagePercentage} 
                        sx={{ mb: 1, height: 8, borderRadius: 4 }}
                        color={storagePercentage > 80 ? 'error' : storagePercentage > 60 ? 'warning' : 'primary'}
                      />
                      <Typography variant="caption">
                        {systemStatus.storageUsed}GB / {systemStatus.storageTotal}GB
                      </Typography>
                    </Box>

                    <Divider />

                    <Button
                      variant="contained"
                      startIcon={<Backup />}
                      onClick={handleBackupNow}
                      fullWidth
                      sx={{ mb: 1 }}
                    >
                      Backup Data
                    </Button>
                    
                    <Button
                      variant="outlined"
                      startIcon={<Assessment />}
                      fullWidth
                    >
                      Generate Report
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Phone color="primary" sx={{ mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6">Contact Information</Typography>
                      <Typography variant="body2" color="textSecondary">
                        How customers and partners can reach you
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="email"
                        label="Business Email"
                        type="email"
                        value={companyData.email}
                        onChange={handleCompanyChange}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: <Email sx={{ color: 'action.active', mr: 1 }} />
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="phone"
                        label="Phone Number"
                        value={companyData.phone}
                        onChange={handleCompanyChange}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          startAdornment: <Phone sx={{ color: 'action.active', mr: 1 }} />
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="address"
                        label="Business Address"
                        value={companyData.address}
                        onChange={handleCompanyChange}
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                        InputProps={{
                          startAdornment: <LocationOn sx={{ color: 'action.active', mr: 1, alignSelf: 'flex-start', mt: 1 }} />
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Legal & Regional */}
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Lock color="primary" sx={{ mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6">Legal & Regional Settings</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Legal identifiers and localization preferences
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="taxId"
                        label="Tax Identification Number"
                        value={companyData.taxId}
                        onChange={handleCompanyChange}
                        fullWidth
                        variant="outlined"
                        helperText="Your business tax ID"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="registrationNumber"
                        label="Business Registration Number"
                        value={companyData.registrationNumber}
                        onChange={handleCompanyChange}
                        fullWidth
                        variant="outlined"
                        helperText="Official business registration ID"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Currency</InputLabel>
                        <Select
                          name="currency"
                          value={companyData.currency}
                          onChange={handleCompanyChange}
                          label="Currency"
                          startAdornment={<AttachMoney sx={{ color: 'action.active', mr: 1 }} />}
                        >
                          <MenuItem value="TZS">🇹🇿 TZS - Tanzanian Shilling</MenuItem>
                          <MenuItem value="USD">🇺🇸 USD - US Dollar</MenuItem>
                          <MenuItem value="EUR">🇪🇺 EUR - Euro</MenuItem>
                          <MenuItem value="KES">🇰🇪 KES - Kenyan Shilling</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Language</InputLabel>
                        <Select
                          name="language"
                          value={companyData.language}
                          onChange={handleCompanyChange}
                          label="Language"
                          startAdornment={<Language sx={{ color: 'action.active', mr: 1 }} />}
                        >
                          <MenuItem value="English">🇬🇧 English</MenuItem>
                          <MenuItem value="Swahili">🇹🇿 Swahili</MenuItem>
                          <MenuItem value="French">🇫🇷 French</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Time Zone</InputLabel>
                        <Select
                          name="timeZone"
                          value={companyData.timeZone}
                          onChange={handleCompanyChange}
                          label="Time Zone"
                          startAdornment={<Schedule sx={{ color: 'action.active', mr: 1 }} />}
                        >
                          <MenuItem value="Africa/Dar_es_Salaam">🌍 East Africa Time (UTC+3)</MenuItem>
                          <MenuItem value="Africa/Nairobi">🌍 East Africa Time (UTC+3)</MenuItem>
                          <MenuItem value="UTC">🌐 Universal Time (UTC)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Save />}
                      onClick={handleSaveCompany}
                      sx={{ minWidth: 200 }}
                    >
                      Save Company Information
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* System Settings Tab */}
        <TabPanel value={tabValue} index={1}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Settings color="primary" sx={{ mr: 2, fontSize: 28 }} />
                <Box>
                  <Typography variant="h6">System Preferences</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Configure how your system behaves
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={4}>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Notifications color="primary" sx={{ mr: 2 }} />
                      <Typography variant="h6">Notifications</Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.emailNotifications}
                          onChange={handleSystemChange('emailNotifications')}
                          color="primary"
                        />
                      }
                      label="Email Notifications"
                    />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Receive system alerts and updates via email
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Cloud color="primary" sx={{ mr: 2 }} />
                      <Typography variant="h6">Backup</Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.autoBackup}
                          onChange={handleSystemChange('autoBackup')}
                          color="primary"
                        />
                      }
                      label="Automatic Backup"
                    />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Daily automatic backup of all system data
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <People color="primary" sx={{ mr: 2 }} />
                      <Typography variant="h6">Access Control</Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.allowGuestAccess}
                          onChange={handleSystemChange('allowGuestAccess')}
                          color="primary"
                        />
                      }
                      label="Guest Access"
                    />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Allow read-only access for guests
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Session Timeout (minutes)"
                    type="number"
                    value={systemSettings.sessionTimeout}
                    onChange={handleSystemChange('sessionTimeout')}
                    fullWidth
                    variant="outlined"
                    helperText="User sessions will expire after this time"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Data Retention (days)"
                    type="number"
                    value={systemSettings.dataRetention}
                    onChange={handleSystemChange('dataRetention')}
                    fullWidth
                    variant="outlined"
                    helperText="How long to keep deleted records"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Save />}
                  onClick={handleSaveSystem}
                  sx={{ minWidth: 200 }}
                >
                  Save System Settings
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Security & Backup Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Security color="primary" sx={{ mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6">Security Settings</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Protect your system and data
                      </Typography>
                    </Box>
                  </Box>

                  <Stack spacing={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.enableTwoFactor}
                          onChange={handleSystemChange('enableTwoFactor')}
                          color="primary"
                        />
                      }
                      label="Two-Factor Authentication"
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.maintenanceMode}
                          onChange={handleSystemChange('maintenanceMode')}
                          color="warning"
                        />
                      }
                      label={
                        <Box>
                          <Typography>Maintenance Mode</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Restrict system access for maintenance
                          </Typography>
                        </Box>
                      }
                    />

                    <TextField
                      label="Max Login Attempts"
                      type="number"
                      value={systemSettings.maxLoginAttempts}
                      onChange={handleSystemChange('maxLoginAttempts')}
                      fullWidth
                      variant="outlined"
                      helperText="Lock account after this many failed attempts"
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Backup color="primary" sx={{ mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6">Backup & Recovery</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Secure your data with regular backups
                      </Typography>
                    </Box>
                  </Box>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Last Backup
                      </Typography>
                      <Chip 
                        label={systemStatus.lastBackup}
                        color="success"
                        icon={<CheckCircle />}
                      />
                    </Box>

                    <Button
                      variant="contained"
                      startIcon={<Backup />}
                      onClick={handleBackupNow}
                      fullWidth
                      size="large"
                    >
                      Backup Now
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<Storage />}
                      fullWidth
                    >
                      Download Backup
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<Assessment />}
                      fullWidth
                    >
                      Security Audit
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default CompanySettings;
